import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Login() {
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/";
  const [waNumber, setWaNumber] = useState("");
  useEffect(() => {
    api.get("/config").then((r) => setWaNumber(r.data.whatsapp || "")).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo size="lg" className="items-center text-center" />
          </div>

          <div className="bg-white border border-brown-border rounded-2xl p-7 md:p-9 shadow-[0_4px_28px_-12px_rgba(59,43,32,0.15)]">
            <RetailerOtpForm
              onSuccess={() => nav(from, { replace: true })}
              loginWithToken={loginWithToken}
            />

            <div className="mt-6 pt-5 border-t border-brown-border">
              <p className="text-xs text-brown-medium leading-relaxed">
                <span className="font-semibold text-brown">Retailer accounts are created by our team.</span>
                {waNumber && (
                  <>
                    <br />
                    Contact us on WhatsApp <span className="text-brand-red font-semibold">{waNumber}</span> to become a partner.
                  </>
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RetailerOtpForm({ onSuccess, loginWithToken }) {
  const [phone, setPhone] = useState("");
  const [step, setStep] = useState(1);   // 1: enter phone, 2: enter OTP
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [resendSecs, setResendSecs] = useState(0);
  const [otpSentBanner, setOtpSentBanner] = useState(false);
  const widgetInitedRef = React.useRef(false);
  const otpRefs = React.useRef([]);

  // Ensure the MSG91 widget script is loaded exactly once per page.
  const ensureScriptLoaded = () => new Promise((resolve, reject) => {
    if (window.initSendOTP) return resolve();
    const existing = document.querySelector('script[data-msg91-widget]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load MSG91 widget")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://verify.msg91.com/otp-provider.js";
    s.async = true;
    s.dataset.msg91Widget = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load MSG91 widget"));
    document.head.appendChild(s);
  });

  // Mount the MSG91 widget in "headless" mode (exposeMethods:true means the
  // widget does NOT render its own modal — we own the UI here and call the
  // exposed window.verifyOtp / window.retryOtp methods).
  const startWidget = (cfg) => new Promise((resolve, reject) => {
    if (widgetInitedRef.current) return resolve();
    const configuration = {
      widgetId: cfg.widget_id,
      tokenAuth: cfg.widget_token,
      identifier: cfg.identifier,   // e.g. "919999999999"
      exposeMethods: true,
      // We rely on window.verifyOtp instead of the built-in success callback,
      // so this success handler is only invoked if MSG91's fallback UI ends up
      // running (defensive fallback for older widget builds).
      success: () => {},
      failure: (err) => {
        console.error("[msg91] widget failure", err);
      },
    };
    try {
      window.initSendOTP(configuration);
      // Give the widget a beat to expose its methods on window before we resolve.
      const waitFor = () => {
        if (window.verifyOtp && window.retryOtp) return resolve();
        setTimeout(waitFor, 100);
      };
      waitFor();
      // Safety timeout — resolve anyway so we don't hang the UI forever.
      setTimeout(() => resolve(), 2500);
      widgetInitedRef.current = true;
    } catch (err) {
      widgetInitedRef.current = false;
      reject(err);
    }
  });

  const finishLogin = async (accessToken) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/retailer/verify-widget-token", {
        phone, access_token: accessToken,
      });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      onSuccess(data.user);
    } catch (err) {
      setError(err?.response?.data?.detail || "Verification failed");
      setBusy(false);
    }
  };

  const sendOtp = async (e) => {
    if (e) e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await ensureScriptLoaded();
      const { data } = await api.post("/auth/retailer/check-mobile", { phone });
      await startWidget(data);
      setStep(2);
      setOtp("");
      setResendSecs(30);
      setOtpSentBanner(true);
      setTimeout(() => otpRefs.current[0]?.focus(), 60);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Could not send OTP");
    } finally {
      setBusy(false);
    }
  };

  // Countdown for the "Resend OTP" cooldown.
  React.useEffect(() => {
    if (resendSecs <= 0) return undefined;
    const t = setTimeout(() => setResendSecs((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendSecs]);

  const submitOtp = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    setError("");
    const code = String(otp || "").replace(/\D/g, "");
    if (code.length < 4) {
      setError("Please enter the complete OTP");
      return;
    }
    if (!window.verifyOtp) {
      setError("OTP service not ready. Please tap Resend OTP and try again.");
      return;
    }
    setBusy(true);
    window.verifyOtp(
      code,
      (data) => {
        // MSG91 returns { message: <JWT access token>, type: "success" } on success.
        const accessToken = data?.message || data?.token || data;
        finishLogin(accessToken);
      },
      (err) => {
        console.warn("[msg91] verifyOtp failure", err);
        const msg = (err && (err.message || err.type)) || "Incorrect or expired OTP. Try again.";
        setError(String(msg));
        setBusy(false);
        setOtp("");
        setTimeout(() => otpRefs.current[0]?.focus(), 40);
      }
    );
  };

  const resend = () => {
    if (resendSecs > 0 || busy) return;
    if (!window.retryOtp) {
      setError("OTP service not ready. Please Change Number and try again.");
      return;
    }
    setError("");
    setBusy(true);
    window.retryOtp(
      "11",   // "11" = SMS retry channel (MSG91 default)
      () => {
        setBusy(false);
        setResendSecs(30);
        setOtp("");
        setOtpSentBanner(true);
        toast.success("OTP re-sent");
        setTimeout(() => otpRefs.current[0]?.focus(), 40);
      },
      (err) => {
        setBusy(false);
        setError((err && (err.message || err.type)) || "Could not resend OTP");
      }
    );
  };

  const changeNumber = () => {
    // Clean up widget-mounted state so the next attempt re-initialises the SDK.
    widgetInitedRef.current = false;
    setStep(1);
    setOtp("");
    setError("");
    setResendSecs(0);
    setOtpSentBanner(false);
  };

  const setOtpDigit = (idx, val) => {
    const digit = String(val || "").replace(/\D/g, "").slice(-1);
    const arr = otp.padEnd(6, " ").split("");
    arr[idx] = digit || " ";
    const next = arr.join("").replace(/\s/g, "");
    setOtp(next);
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const t = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 6);
    if (!t) return;
    e.preventDefault();
    setOtp(t);
    const target = Math.min(t.length, 5);
    otpRefs.current[target]?.focus();
    // If it's a full 6-digit code, auto-submit for convenience.
    if (t.length === 6) setTimeout(() => submitOtp(), 50);
  };

  const handleOtpKey = (idx, e) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  if (step === 1) {
    return (
      <form onSubmit={sendOtp} className="space-y-4">
        <h1 className="font-serif text-brown text-2xl mb-1">Retailer Sign In</h1>
        <p className="text-sm text-brown-medium mb-2">Enter your registered mobile number to receive an OTP.</p>
        <div>
          <label className="text-[11px] tracking-[0.2em] uppercase text-brown-medium font-semibold">Mobile Number</label>
          <input
            data-testid="login-phone-input"
            type="tel"
            inputMode="numeric"
            required
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="10-digit mobile"
            className="mt-1.5 w-full h-11 px-4 rounded-lg bg-ivory border border-brown-border text-brown focus:outline-none focus:border-brand-red"
          />
        </div>
        {error && <div data-testid="login-error" className="text-brand-red text-sm bg-brand-red/5 border border-brand-red/20 rounded-lg px-3 py-2.5">{error}</div>}
        <button data-testid="login-send-otp-btn" type="submit" disabled={busy || phone.replace(/\D/g,"").length < 10}
          className="w-full h-12 rounded-full bg-brand-red text-ivory font-semibold text-[13px] tracking-[0.14em] uppercase hover:bg-brand-red-dark transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Send OTP
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={submitOtp} className="space-y-4" data-testid="login-otp-form">
      <h1 className="font-serif text-brown text-2xl mb-1">Enter OTP</h1>
      <p className="text-sm text-brown-medium">
        We&#39;ve sent a verification code to <b>+91 {phone.slice(-10)}</b>.
      </p>

      {otpSentBanner && (
        <div className="text-brown-medium text-xs bg-ivory border border-brown-border/60 rounded-lg px-3 py-2">
          OTP sent successfully. It may take up to a minute to arrive.
        </div>
      )}

      <div className="flex items-center justify-between gap-2" onPaste={handleOtpPaste}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <input
            key={i}
            data-testid={`login-otp-digit-${i}`}
            ref={(el) => (otpRefs.current[i] = el)}
            type="tel"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={1}
            value={otp[i] || ""}
            onChange={(e) => setOtpDigit(i, e.target.value)}
            onKeyDown={(e) => handleOtpKey(i, e)}
            className="w-11 h-12 md:w-12 md:h-14 text-center text-lg md:text-xl font-bold rounded-lg bg-ivory border border-brown-border text-brown focus:outline-none focus:border-brand-red focus:ring-2 focus:ring-brand-red/20"
          />
        ))}
      </div>

      {error && <div data-testid="login-error" className="text-brand-red text-sm bg-brand-red/5 border border-brand-red/20 rounded-lg px-3 py-2.5">{error}</div>}

      <button
        data-testid="login-verify-otp-btn"
        type="submit"
        disabled={busy || otp.length < 4}
        className="w-full h-12 rounded-full bg-brand-red text-ivory font-semibold text-[13px] tracking-[0.14em] uppercase hover:bg-brand-red-dark transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Verify &amp; Sign In
      </button>

      <div className="flex items-center justify-between text-xs text-brown-medium">
        <button
          type="button"
          data-testid="login-change-number-btn"
          onClick={changeNumber}
          className="underline hover:text-brand-red"
        >
          Change number
        </button>
        <button
          type="button"
          data-testid="login-resend-btn"
          onClick={resend}
          disabled={resendSecs > 0 || busy}
          className="underline hover:text-brand-red disabled:no-underline disabled:opacity-60"
        >
          {resendSecs > 0 ? `Resend OTP in ${resendSecs}s` : "Resend OTP"}
        </button>
      </div>
    </form>
  );
}

function AdminEmailForm() {
  // Removed from the public login page. Admin sign-in lives at the hidden
  // /secure-admin route (see AdminLogin.jsx). Kept intentionally absent here
  // so nothing on the retailer site references an admin portal.
  return null;
}
